import React from 'react'
import { Show, TextField, DateField, EditButton } from '@refinedev/antd'
import { useShow, useOne, useList } from '@refinedev/core'
import { Typography, Tag, Table } from 'antd'
import type { Tables } from '@muaa/shared'

type Rsvp = Tables<'rsvps'>
type Profile = Tables<'profiles'>
type Event = Tables<'events'>
type RsvpSession = Tables<'rsvp_sessions'>
type EventSession = Tables<'event_sessions'>

const { Title } = Typography

const STATUS_COLORS: Record<string, string> = {
  confirmed: 'green',
  pending: 'orange',
  cancelled: 'red',
  waitlisted: 'blue',
}

export function RsvpShow() {
  const { query } = useShow<Rsvp>()
  const isLoading = query.isLoading
  const record = query.data?.data

  // Resolve user_id -> profile name
  const { query: profileQuery } = useOne<Profile>({
    resource: 'profiles',
    id: record?.user_id ?? '',
    meta: { idColumnName: 'user_id' },
    queryOptions: { enabled: !!record?.user_id },
  })

  // Resolve event_id -> the event itself, so we show WHICH event this RSVP is
  // for (its title) rather than a bare UUID.
  const { query: eventQuery } = useOne<Event>({
    resource: 'events',
    id: record?.event_id ?? '',
    queryOptions: { enabled: !!record?.event_id },
  })
  const event = eventQuery.data?.data

  // Load rsvp_sessions join rows for this rsvp
  const { query: rsvpSessionsQuery } = useList<RsvpSession>({
    resource: 'rsvp_sessions',
    filters: [{ field: 'rsvp_id', operator: 'eq', value: record?.id }],
    queryOptions: { enabled: !!record?.id },
    pagination: { mode: 'off' },
  })
  const rsvpSessionsLoading = rsvpSessionsQuery.isLoading

  const sessionIds = React.useMemo(
    () => (rsvpSessionsQuery.data?.data ?? []).map((rs: RsvpSession) => rs.session_id),
    [rsvpSessionsQuery.data],
  )

  // Load the event session details for the linked sessions
  const { query: sessionsQuery } = useList<EventSession>({
    resource: 'event_sessions',
    filters:
      sessionIds.length > 0
        ? [{ field: 'id', operator: 'in', value: sessionIds }]
        : [{ field: 'id', operator: 'eq', value: '__none__' }],
    queryOptions: { enabled: sessionIds.length > 0 },
    pagination: { mode: 'off' },
  })
  const sessionsLoading = sessionsQuery.isLoading

  const sessions = sessionsQuery.data?.data ?? []

  return (
    <Show isLoading={isLoading} headerButtons={({ defaultButtons }) => <>{defaultButtons}<EditButton /></>}>
      <Title level={5}>Attendee</Title>
      <TextField
        value={
          profileQuery.data?.data?.name ??
          record?.user_id ??
          '—'
        }
      />

      <Title level={5}>Event</Title>
      <TextField value={event?.title ?? record?.event_id ?? '—'} />
      {event?.location ? (
        <div style={{ color: '#667', fontSize: 12, marginTop: 2 }}>{event.location}</div>
      ) : null}

      <Title level={5}>Guests</Title>
      <TextField value={record?.guests != null ? String(record.guests) : '0'} />

      <Title level={5}>Meal</Title>
      <TextField value={record?.meal ?? '—'} />

      <Title level={5}>Status</Title>
      {record?.status ? (
        <Tag color={STATUS_COLORS[record.status] ?? 'default'}>{record.status}</Tag>
      ) : (
        <TextField value="—" />
      )}

      <Title level={5}>Created</Title>
      {record?.created_at ? (
        <DateField value={record.created_at} format="MMM D, YYYY h:mm A" />
      ) : (
        <TextField value="—" />
      )}

      <Title level={5} style={{ marginTop: 24 }}>
        Selected Sessions
      </Title>
      {rsvpSessionsLoading || sessionsLoading ? (
        <TextField value="Loading sessions…" />
      ) : sessions.length === 0 ? (
        <TextField value="No sessions selected" />
      ) : (
        <Table
          dataSource={sessions}
          rowKey="id"
          size="small"
          pagination={false}
          style={{ marginTop: 8 }}
        >
          <Table.Column<EventSession> dataIndex="title" title="Title" />
          <Table.Column<EventSession> dataIndex="day" title="Day" />
          <Table.Column<EventSession> dataIndex="start_time" title="Start Time" />
          <Table.Column<EventSession> dataIndex="location" title="Location" />
        </Table>
      )}
    </Show>
  )
}
